import { useState } from 'react';
import { CartItem, OrderForm } from '../../types';
import Button from '../base/Button';
import Input from '../base/Input';

interface CartProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onClearCart: () => void;
}

export default function Cart({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart
}: CartProps) {
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderForm, setOrderForm] = useState<OrderForm>({
    fullName: '',
    phone: '',
    email: '',
    pickupNote: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<OrderForm>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<OrderForm> = {};
    
    if (!orderForm.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!orderForm.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare order data for Supabase
      const orderData = {
        customer_name: orderForm.fullName,
        customer_phone: orderForm.phone,
        customer_email: orderForm.email || null,
        pickup_note: orderForm.pickupNote || null,
        items: cartItems.map(item => ({
          drink_name: item.drink.name,
          milk: item.milk,
          temperature: item.temperature,
          flavor: item.flavor || null,
          quantity: item.quantity
        })),
        total_amount: 0.00,
        status: 'pending'
      };

      // Fixed URL format for Supabase Edge Functions
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/submit-order`;

      console.log('Submitting order to:', functionUrl);
      console.log('Order data:', orderData);

      // Submit order using the correct function URL
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(orderData)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Order submitted successfully:', result);
        setOrderSubmitted(true);
        onClearCart();
        setOrderForm({
          fullName: '',
          phone: '',
          email: '',
          pickupNote: ''
        });
      } else {
        const errorText = await response.text();
        console.error('Order submission failed:', response.status, errorText);
        throw new Error(`Failed to submit order: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      alert(`Failed to submit order: ${error.message}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowCheckout(false);
    setOrderSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-cream shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-script text-navy">
              {showCheckout ? 'Checkout' : 'Your Cart'}
            </h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-close-line text-xl text-navy"></i>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {orderSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-medium text-navy mb-2">Order Received!</h3>
                <p className="text-navy/70">We'll text you if we have any questions.</p>
              </div>
            ) : showCheckout ? (
              <form onSubmit={handleSubmitOrder} className="space-y-4">
                <Input
                  label="Full Name"
                  required
                  value={orderForm.fullName}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, fullName: e.target.value }))}
                  error={errors.fullName}
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  required
                  value={orderForm.phone}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, phone: e.target.value }))}
                  error={errors.phone}
                />
                
                <Input
                  label="Email (Optional)"
                  type="email"
                  value={orderForm.email}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, email: e.target.value }))}
                />
                
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Pickup Note (Optional)
                  </label>
                  <textarea
                    value={orderForm.pickupNote}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setOrderForm(prev => ({ ...prev, pickupNote: e.target.value }));
                      }
                    }}
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm resize-none"
                    placeholder="Any special instructions..."
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {orderForm.pickupNote?.length || 0}/500 characters
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-medium text-navy">Total:</span>
                    <span className="text-lg font-medium text-navy">$0.00</span>
                  </div>
                  
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    className="w-full"
                  >
                    Submit Order
                  </Button>
                </div>
              </form>
            ) : (
              <>
                {cartItems.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-shopping-cart-line text-4xl text-navy/30 mb-4"></i>
                    <p className="text-navy/70">Your cart is empty</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {cartItems.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl p-4 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-navy">{item.drink.name}</h4>
                            <button
                              onClick={() => onRemoveItem(item.id)}
                              className="text-red-500 hover:text-red-700 whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                          
                          <div className="text-sm text-navy/70 space-y-1">
                            <div>Milk: {item.milk}</div>
                            <div>Temperature: {item.temperature}</div>
                            {item.flavor && <div>Flavor: {item.flavor}</div>}
                          </div>
                          
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                                className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 hover:border-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-subtract-line text-xs"></i>
                              </button>
                              <span className="text-sm font-medium text-navy min-w-[1.5rem] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                                className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 hover:border-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-add-line text-xs"></i>
                              </button>
                            </div>
                            <span className="text-sm font-medium text-navy">$0.00</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-medium text-navy">Total:</span>
                        <span className="text-lg font-medium text-navy">$0.00</span>
                      </div>
                      
                      <Button
                        onClick={() => setShowCheckout(true)}
                        className="w-full"
                      >
                        Proceed to Checkout
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
